import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {MoveInspectionDataAssetBody, MoveInspectionDataRequestBody} from '@dms/api/core';

import {AuditDataAssetsFilesBody} from '../../../types/AuditDataAssetsFilesBody';
import {AuditDamageValueBody} from '../../../types/ConditionTypes';
import {getFormFieldName} from './getFormFieldName';

type PhotoParamAssets = {
  paramDefinitionId: string;
  /** Assets belonging to the moved damage under this source photo param. */
  assets: AuditDataAssetsFilesBody[];
};

type MoveDamageCategorySide = {
  categoryId: string;
  typeParamId: string;
  commentParamId?: string;
  typeValue: AuditDamageValueBody[];
  commentValue: AuditDamageValueBody[];
};

export type BuildMoveAuditDamagePayloadInput = {
  damageId: string;
  /** Damage type value to persist under the target part. */
  type: string;
  /** Damage comment text; empty/nullish clears the comment on the target part. */
  comment: string | null;
  order: number;
  source: MoveDamageCategorySide & {photoParams: PhotoParamAssets[]};
  /** Target photo param ids in the same order (location, detail, ...) as `source.photoParams`. */
  target: MoveDamageCategorySide & {photoParamIds: string[]};
};

/** A Final Form field value change to keep the form and redux slice in sync with the moved damage. */
export type MoveDamageFieldUpdate = {
  fieldName: string;
  value: AuditDamageValueBody[];
};

/** A local re-keying of assets in the carAudit redux slice from a source to a target photo param. */
export type MoveDamageAssetRelocation = {
  fromCategoryId: string;
  fromParamId: string;
  toCategoryId: string;
  toParamId: string;
  assets: AuditDataAssetsFilesBody[];
};

export type BuiltMoveAuditDamagePayload = {
  requestBody: MoveInspectionDataRequestBody;
  fieldUpdates: MoveDamageFieldUpdate[];
  assetRelocations: MoveDamageAssetRelocation[];
};

const removeDamage = (value: AuditDamageValueBody[], damageId: string): AuditDamageValueBody[] =>
  value.filter((damage) => damage.damageId !== damageId);

const addDamage = (
  value: AuditDamageValueBody[],
  damage: AuditDamageValueBody
): AuditDamageValueBody[] => [...removeDamage(value, damage.damageId), damage];

type FieldMoveSide = {
  categoryId: string;
  paramDefinitionId: string;
  value: AuditDamageValueBody[];
};

const toFieldMove = (
  source: FieldMoveSide,
  target: FieldMoveSide
): MoveInspectionDataRequestBody['fieldMoves'][number] => ({
  sourceCategoryId: source.categoryId,
  sourceParamDefinitionId: source.paramDefinitionId,
  sourceValue: JSON.stringify(source.value),
  targetCategoryId: target.categoryId,
  targetParamDefinitionId: target.paramDefinitionId,
  targetValue: JSON.stringify(target.value),
});

/**
 * Builds the request body for the move-damage endpoint together with the local state updates
 * (Final Form field values + carAudit asset re-keying) needed to reflect the move immediately.
 *
 * The type and comment values are relocated from the source to the target part (removed from the
 * source, added to the target), and each photo asset is mapped to the matching target photo param
 * by position.
 */
export const buildMoveAuditDamagePayload = (
  input: BuildMoveAuditDamagePayloadInput
): BuiltMoveAuditDamagePayload => {
  const {damageId, type, comment, order, source, target} = input;

  const sourceType = removeDamage(source.typeValue, damageId);
  const targetType = addDamage(target.typeValue, {damageId, type, order});

  const fieldUpdates: MoveDamageFieldUpdate[] = [
    {fieldName: getFormFieldName(source.categoryId, source.typeParamId), value: sourceType},
    {fieldName: getFormFieldName(target.categoryId, target.typeParamId), value: targetType},
  ];

  const fieldMoves: MoveInspectionDataRequestBody['fieldMoves'] = [
    toFieldMove(
      {categoryId: source.categoryId, paramDefinitionId: source.typeParamId, value: sourceType},
      {categoryId: target.categoryId, paramDefinitionId: target.typeParamId, value: targetType}
    ),
  ];

  if (source.commentParamId && target.commentParamId) {
    const sourceComment = removeDamage(source.commentValue, damageId);
    const targetComment = isNotNilOrEmpty(comment)
      ? addDamage(target.commentValue, {damageId, type: comment ?? '', order})
      : removeDamage(target.commentValue, damageId);

    fieldUpdates.push(
      {fieldName: getFormFieldName(source.categoryId, source.commentParamId), value: sourceComment},
      {fieldName: getFormFieldName(target.categoryId, target.commentParamId), value: targetComment}
    );

    fieldMoves.push(
      toFieldMove(
        {
          categoryId: source.categoryId,
          paramDefinitionId: source.commentParamId,
          value: sourceComment,
        },
        {
          categoryId: target.categoryId,
          paramDefinitionId: target.commentParamId,
          value: targetComment,
        }
      )
    );
  }

  const assetMoves: MoveInspectionDataAssetBody[] = [];
  const assetRelocations: MoveDamageAssetRelocation[] = [];
  const availableTargetPhotoParamIds = target.photoParamIds.filter(isNotNilOrEmpty);

  source.photoParams.forEach((sourcePhotoParam, index) => {
    if (sourcePhotoParam.assets.length === 0) {
      return;
    }

    // Prefer the matching slot by position; fall back to the last available target photo param so
    // a target with fewer photo slots never silently drops (orphans) the moved damage's photos.
    const targetParamId =
      availableTargetPhotoParamIds.at(index) ?? availableTargetPhotoParamIds.at(-1);

    if (isNil(targetParamId)) {
      return;
    }

    assetRelocations.push({
      fromCategoryId: source.categoryId,
      fromParamId: sourcePhotoParam.paramDefinitionId,
      toCategoryId: target.categoryId,
      toParamId: targetParamId,
      assets: sourcePhotoParam.assets,
    });

    sourcePhotoParam.assets.forEach((asset) => {
      if (isNotNilOrEmpty(asset.imageId)) {
        assetMoves.push({
          imageId: asset.imageId ?? '',
          targetCategoryId: target.categoryId,
          targetParamDefinitionId: targetParamId,
        });
      }
    });
  });

  return {
    requestBody: {fieldMoves, assetMoves},
    fieldUpdates,
    assetRelocations,
  };
};
